import { Injectable, inject } from '@angular/core';
import { ErrorResponse, MessagesDataService } from '@enp/platform-api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, filter, map, of, switchMap } from 'rxjs';

import { mapErrorResponseApiModelToStoreModel } from '@domain/common';
import {
  SystemMessageCategoryStoreModelEnum,
  mapMessageApiModelToStoreModel,
  mapPostManualMessageRequestStoreModelToApiModel,
} from '@domain/messages';

import { getIsPollingContext } from '@shared/interceptors';
import { toUtcIso } from '@shared/utils/zoned-date';

import { CoreActions } from '@stores/core';
import { MessagesGetParticipantMessagesStoreModel, MessagesGetSystemMessagesStoreModel } from '@stores/messages/models';

import { MessagesActions } from './messages.actions';
import { fromMessages } from './messages.selectors';

@Injectable()
export class MessagesEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #messagesDataService = inject(MessagesDataService);
  readonly #translateService = inject(TranslateService);

  public getParticipantUnreadMessages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.getParticipantUnreadMessages, MessagesActions.getParticipantMessagesSuccess),
      switchMap((action) => {
        const isPolling = action.type === MessagesActions.getParticipantUnreadMessages.type ? action.isPolling : false;

        return this.#messagesDataService
          .getParticipantUnreadMessages(undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map(({ count }) => MessagesActions.getParticipantUnreadMessagesSuccess({ count })),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                MessagesActions.getParticipantUnreadMessagesFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          );
      })
    );
  });

  public getSystemMessages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.getSystemMessages),
      concatMap(({ action, isPolling }) =>
        this.#messagesDataService
          .getSystemMessages(...this.mapMessagesGetSystemMessagesStoreModelToApiModel(action), undefined, undefined, {
            context: getIsPollingContext(!!isPolling),
          })
          .pipe(
            map((messages) =>
              MessagesActions.getSystemMessagesSuccess({
                messages: messages.entries.map((message) => mapMessageApiModelToStoreModel(message)),
                action,
                totalCount: messages.totalCount,
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                MessagesActions.getSystemMessagesFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getParticipantMessages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.getParticipantMessages),
      concatMap(({ action }) =>
        this.#messagesDataService.getParticipantMessages(...this.mapMessagesGetParticipantMessagesStoreModelToApiModel(action)).pipe(
          map((messages) =>
            MessagesActions.getParticipantMessagesSuccess({
              messages: messages.entries.map((message) => mapMessageApiModelToStoreModel(message)),
              action,
              totalCount: messages.totalCount,
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              MessagesActions.getParticipantMessagesFail({
                errorMsg: mapErrorResponseApiModelToStoreModel(error),
              })
            )
          )
        )
      )
    );
  });

  public requestFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        MessagesActions.getSystemMessagesFail,
        MessagesActions.getParticipantUnreadMessagesFail,
        MessagesActions.getParticipantMessagesFail
      ),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public postManualMessage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.postManualMessage),
      switchMap(({ message }) =>
        this.#messagesDataService.postManualMessage(mapPostManualMessageRequestStoreModelToApiModel(message)).pipe(
          map(() => MessagesActions.postManualMessageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              MessagesActions.postManualMessageFail({
                errorCode: error?.code || '',
                errorMsg: error?.message || '',
              })
            )
          )
        )
      )
    );
  });

  public postManualMessageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.postManualMessageSuccess),
      switchMap(() => this.#translateService.get('messages.shared.toasts.success')),
      map((message) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public reloadGeneralSystemMessagesOnPostManualMessageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.postManualMessageSuccess),
      concatLatestFrom(() => this.#store.select(fromMessages.selectSystemMessages$)),
      map(([_, systemMessages]) => systemMessages),
      filter(
        (systemMessages) => !!systemMessages?.action && systemMessages.action.category === SystemMessageCategoryStoreModelEnum.GENERAL
      ),
      switchMap((systemMessages) => {
        return of(
          MessagesActions.getSystemMessages({
            action: systemMessages!.action!,
            isPolling: false,
          })
        );
      })
    );
  });

  public postManualMessageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(MessagesActions.postManualMessageFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('messages.shared.toasts.error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  private mapMessagesGetSystemMessagesStoreModelToApiModel(request: MessagesGetSystemMessagesStoreModel) {
    return [
      request.limit,
      request.offset,
      request.sortBy,
      request.category,
      request.severities,
      request.senders,
      request.types,
      request.receivedFromDateTime && toUtcIso(request.receivedFromDateTime)!,
      request.receivedToDateTime && toUtcIso(request.receivedToDateTime)!,
      request.searchQuery,
      request.hasAttachment,
    ] as const;
  }

  private mapMessagesGetParticipantMessagesStoreModelToApiModel(request: MessagesGetParticipantMessagesStoreModel) {
    return [
      request.limit,
      request.offset,
      request.sortBy,
      request.severities,
      request.senders,
      request.types,
      request.receivedFromDateTime && toUtcIso(request.receivedFromDateTime)!,
      request.receivedToDateTime && toUtcIso(request.receivedToDateTime)!,
      request.searchQuery,
      request.hasAttachment,
    ] as const;
  }
}
